import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useMotionValue } from 'framer-motion';
import { PlusCircleIcon } from '@heroicons/react/outline';

const Milestone = ({ hideIcon }) => {
  const milestones = [
    { 
      year: '1949 - 1960\'lar', 
      title: 'Başlangıç ve Temeller',
      description: 'Haydar Yücel\'in fiberglasın otomotiv ve tekne üretimindeki potansiyelini keşfetmesiyle ticari faaliyetler başladı. 50\'ler ve 60\'larda yapı sektöründe çatı malzemeleri, boya ve makine ekipmanları pazarlayarak kompozit teknolojileriyle ilk temas kuruldu.',
      image: '/images/history/1.jpeg',
      icon: '🏭'
    },
    { 
      year: '1970\'ler', 
      title: 'CTP Sektörüne Giriş',
      description: 'Erdem ve Erdal Yücel\'in katılımıyla firma CTP sektörüne stratejik yönelim kararı aldı. 1971\'den itibaren CTP pazarlamasını Türkiye genelinde yaygınlaştırarak sektörde öncü bir konum elde edildi.',
      image: '/images/history/2.jpg',
      icon: '🚀'
    },
    { 
      year: '1980\'ler', 
      title: 'Üretime Geçiş ve Büyüme',
      description: '1983 yılında Fibrosan CTP Sanayi ve Ticaret A.Ş.\'nin kuruluşuyla üretim faaliyetlerine başlandı. 1987\'de Fiberplast Plastik Ürünler Ltd. Şti.\'nin kurulmasıyla CTP silo ve tank üretimi alanında uzmanlaşma sağlandı.',
      image: '/images/history/3.jpg',
      icon: '🏗️'
    },
    { 
      year: '1990\'lar', 
      title: 'Avrupa\'ya Açılış',
      description: '1999\'da Almanya\'da Fibrosan GmbH\'nin kuruluşuyla Avrupa pazarına doğrudan giriş yapıldı. Bu adım, uluslararası pazardaki varlığımızı güçlendiren önemli bir kilometre taşı oldu.',
      image: '/images/history/4.jpg',
      icon: '🇪🇺'
    },
    { 
      year: '2000\'ler', 
      title: 'Küresel Genişleme',
      description: 'Fibrosan, İngiltere ve Amerika pazarlarında büyük ilgi gördü. 2007\'de İngiltere\'de Fibrosan UK Ltd.\'nin kuruluşuyla Avrupa\'daki faaliyetlerimiz genişletildi ve küresel varlığımız pekiştirildi.',
      image: '/images/history/5.jpeg',
      icon: '🌍'
    },
    { 
      year: '2010 - Günümüz', 
      title: 'Dünya Projeleri ve Liderlik',
      description: 'Fibrosan ve Fiberplast, 2010 sonrası dev projelerde yer alarak uluslararası ödüller kazandı. Amerika pazarındaki başarılar Fibrosan Incorporated ile taçlandırıldı. Bugün 6 kıtada faaliyet gösteren bir kompozit teknolojileri lideriyiz.',
      image: '/images/history/6.jpg',
      icon: '🏆'
    }
  ];

  const { scrollYProgress } = useScroll();
  const lineColor = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    ['#dc2626', '#ef4444', '#b91c1c']
  );

  const lineWidth = useTransform(
    scrollYProgress,
    [0, 1],
    ['0.15rem', '0.25rem']
  );

  const [showIcon, setShowIcon] = useState(false);
  const [activeMilestone, setActiveMilestone] = useState(0);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const handleMouseMove = (event) => {
    mouseX.set(event.clientX);
    mouseY.set(event.clientY);
  };

  const imageX1 = useTransform(mouseX, [0, window.innerWidth], [-30, 30]);
  const imageY1 = useTransform(mouseY, [0, window.innerHeight], [-30, 30]);

  const imageX2 = useTransform(mouseX, [0, window.innerWidth], [30, -30]);
  const imageY2 = useTransform(mouseY, [0, window.innerHeight], [30, -30]);

  useEffect(() => {
    const handleScroll = () => {
      const screenWidth = window.innerWidth;
      let scrollThresholdStart, scrollThresholdEnd;

      if (screenWidth >= 1024) {
        scrollThresholdStart = 0.15;
        scrollThresholdEnd = 1.1;
      } else if (screenWidth >= 768) {
        scrollThresholdStart = 0.18;
        scrollThresholdEnd = 0.85;
      } else {
        scrollThresholdStart = 0.15;
        scrollThresholdEnd = 0.8;
      }

      const unsubscribe = scrollYProgress.onChange((latest) => {
        if (latest > scrollThresholdStart && latest < scrollThresholdEnd) {
          setShowIcon(true);
        } else {
          setShowIcon(false);
        }

        // Aktif milestone'u belirle
        const milestoneIndex = Math.floor(latest * milestones.length);
        if (milestoneIndex >= 0 && milestoneIndex < milestones.length) {
          setActiveMilestone(milestoneIndex);
        }
      });

      return () => unsubscribe();
    };

    handleScroll();
    window.addEventListener('resize', handleScroll);

    return () => window.removeEventListener('resize', handleScroll);
  }, [scrollYProgress, milestones.length]);

  return (
    <div 
      className="relative font-montserrat w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      onMouseMove={handleMouseMove}
    >
      {/* Subtle Background Images */}
      <div className="absolute inset-0 overflow-hidden opacity-20 pointer-events-none">
        <motion.img
          src="/images/abstract/factory1.png"
          alt="Factory Image 1"
          className="absolute top-0 left-16 w-[20rem] h-[20rem] md:w-[30rem] md:h-[30rem] object-contain"
          style={{ x: imageX1, y: imageY1 }}
        />
        <motion.img
          src="/images/abstract/factory2.png"
          alt="Factory Image 2"
          className="absolute bottom-0 right-16 w-[20rem] h-[20rem] md:w-[30rem] md:h-[30rem] object-contain"
          style={{ x: imageX2, y: imageY2 }}
        />
      </div>

      {/* Vertical Timeline Line */}
      <div className="relative">
        <motion.div
          className="absolute left-1/2 transform -translate-x-1/2 h-full w-[0.15rem]"
          style={{
            backgroundColor: lineColor,
            width: lineWidth,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        />
        
        {/* Timeline Nodes */}
        <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-4 flex flex-col items-center justify-between">
          {milestones.map((_, index) => (
            <motion.div
              key={index}
              className="absolute flex items-center justify-center"
              style={{
                top: `${(index * 100) / (milestones.length - 1)}%`,
              }}
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className={`w-6 h-6 rounded-full border-4 ${
                index <= activeMilestone 
                  ? 'bg-red-600 border-red-700' 
                  : 'bg-white border-gray-300'
              } shadow-lg transition-all duration-300`}>
                {index <= activeMilestone && (
                  <motion.div
                    className="w-2 h-2 bg-white rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.3 }}
                  />
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Timeline Content */}
        <div className="relative z-10 pb-32">
          {milestones.map((milestone, index) => (
            <motion.div
              key={index}
              className={`flex flex-col md:flex-row items-center mb-32 md:mb-48 ${
                index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
              }`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: index * 0.1 }}
              viewport={{ once: true, margin: "-100px" }}
            >
              {/* Content Card */}
              <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                <motion.div
                  className={`bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200 hover:shadow-3xl transition-all duration-500 ${
                    index === activeMilestone ? 'ring-2 ring-red-500 ring-opacity-50' : ''
                  }`}
                  whileHover={{ y: -10 }}
                >
                  {/* Card Header */}
                  <div className="p-6 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center shadow-lg">
                        <span className="text-xl text-white">{milestone.icon}</span>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-red-700 uppercase tracking-wider">
                          Dönüm Noktası {index + 1}
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900">{milestone.year}</h3>
                      </div>
                    </div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-2">{milestone.title}</h4>
                  </div>

                  {/* Card Image */}
                  <div className="h-48 md:h-56 overflow-hidden">
                    <img
                      src={milestone.image}
                      alt={milestone.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>

                  {/* Card Content */}
                  <div className="p-6">
                    <p className="text-gray-600 leading-relaxed">{milestone.description}</p>
                    
                    {/* Stats */}
                    <div className="mt-6 pt-6 border-t border-gray-200 grid grid-cols-3 gap-4">
                      {[
                        { label: 'Lokasyon', value: index < 3 ? 'Türkiye' : 'Global' },
                        { label: 'Sektör', value: 'Kompozit' },
                        { label: 'Etki', value: index === 0 ? 'Başlangıç' : index === 5 ? 'Liderlik' : 'Büyüme' }
                      ].map((stat, statIdx) => (
                        <div key={statIdx} className="text-center">
                          <div className="text-sm font-medium text-gray-500">{stat.label}</div>
                          <div className="text-lg font-semibold text-gray-900">{stat.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Connecting Line to Center */}
              <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-0">
                <motion.div
                  className="relative"
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <div className={`absolute top-1/2 transform -translate-y-1/2 w-16 h-[0.15rem] ${
                    index % 2 === 0 ? 'bg-gradient-to-l' : 'bg-gradient-to-r'
                  } from-gray-300 to-transparent`} />
                </motion.div>
              </div>

              {/* Year Indicator */}
              <div className="md:hidden mt-6">
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 bg-red-600 rounded-full border-2 border-red-700" />
                  <div className="text-2xl font-bold text-gray-900">{milestone.year}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      {showIcon && !hideIcon && (
        <motion.div
          className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
        >
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-red-600 to-red-700 rounded-full blur-lg opacity-50 animate-pulse" />
            <PlusCircleIcon className="h-8 w-8 text-white relative z-10" />
          </div>
          <div className="mt-2 text-xs font-medium text-gray-700 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
            Zaman Tünelinde İlerleyin
          </div>
        </motion.div>
      )}

      {/* Active Milestone Indicator */}
      <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <motion.div
          className="bg-white/95 backdrop-blur-sm rounded-xl shadow-xl border border-gray-200 px-6 py-3 flex items-center gap-4"
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-sm font-medium text-gray-700">Şu anda görüntülenen:</div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-600 rounded-full animate-pulse" />
            <div className="font-bold text-gray-900">{milestones[activeMilestone]?.year}</div>
          </div>
          <div className="text-xs text-gray-500">
            {activeMilestone + 1} / {milestones.length}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Milestone;